import { WEATHER_SOURCE_CODES } from "../constants/weatherSourceCodes.js";
import {
  COLOMBIA_IDEAM_LIVE_SOURCE_KEY,
  COLOMBIA_RAINVIEWER_FALLBACK_SOURCE_KEY,
  getWeatherSourceDisplayName,
  getWeatherSourceMetadata,
} from "../constants/colombiaWeatherSourceMetadata.js";

export const WEATHER_SOURCE_ACTION_STATUS = {
  UNSUPPORTED_ACTION: "UNSUPPORTED_ACTION",
};

export const AEROCIVIL_AVIATION_RADAR_SOURCE_KEY = "AEROCIVIL_AVIATION_RADAR";
export const SIATA_GEOPORTAL_SOURCE_KEY = "SIATA_GEOPORTAL";
export const IDEAM_AWS_RADAR_ARCHIVE_SOURCE_KEY = "IDEAM_AWS_RADAR_ARCHIVE";
export const IDEAM_OSPA_VISUALIZER_SOURCE_KEY = "IDEAM_OSPA_VISUALIZER";
export const SATELLITE_PRECIPITATION_FALLBACK_SOURCE_KEY = "SATELLITE_PRECIPITATION_FALLBACK";

/** @type {Record<string, { label: string, description: string, category: string }>} */
export const WEATHER_SOURCE_ACTION_METADATA = {
  "check-health": {
    label: "Check Health",
    description: "Verify that the source endpoint is reachable and usable.",
    category: "health",
  },
  "ideam-discovery": {
    label: "Discover IDEAM Layers",
    description: "Inspect the configured IDEAM ArcGIS MapServer and list candidate radar layers.",
    category: "discovery",
  },
  "rainviewer-refresh": {
    label: "Refresh RainViewer",
    description: "Refresh or validate the latest RainViewer radar metadata.",
    category: "refresh",
  },
  "ideam-archive-catalog": {
    label: "Check IDEAM Archive Catalog",
    description: "Inspect available IDEAM archive catalog metadata.",
    category: "archive",
  },
  "check-local-cache": {
    label: "Check Local Cache",
    description: "Inspect local historical radar frames and generated tile availability.",
    category: "local_replay",
  },
  "siata-discovery": {
    label: "Discover SIATA Layers",
    description: "Inspect SIATA Geoportal layers and source metadata.",
    category: "discovery",
  },
  "siata-ingest": {
    label: "Ingest SIATA Data",
    description: "Run the SIATA ingestion workflow.",
    category: "ingestion",
  },
  "aerocivil-discovery": {
    label: "Discover Aerocivil Radar",
    description: "Probe Aerocivil LEADS portal candidates and update registry health.",
    category: "discovery",
  },
  "aerocivil-cache-sync": {
    label: "Sync Aerocivil Cache",
    description: "Discover and cache the latest Aerocivil aviation radar frame.",
    category: "refresh",
  },
};

/** @type {Record<string, string[]>} */
export const SOURCE_ACTIONS = {
  [COLOMBIA_IDEAM_LIVE_SOURCE_KEY]: ["check-health", "ideam-discovery"],
  [COLOMBIA_RAINVIEWER_FALLBACK_SOURCE_KEY]: ["check-health", "rainviewer-refresh"],
  [IDEAM_AWS_RADAR_ARCHIVE_SOURCE_KEY]: ["check-health", "ideam-archive-catalog"],
  [IDEAM_OSPA_VISUALIZER_SOURCE_KEY]: ["check-health"],
  [SIATA_GEOPORTAL_SOURCE_KEY]: ["check-health", "siata-discovery", "siata-ingest"],
  [SATELLITE_PRECIPITATION_FALLBACK_SOURCE_KEY]: ["check-health"],
  [AEROCIVIL_AVIATION_RADAR_SOURCE_KEY]: [
    "check-health",
    "aerocivil-discovery",
    "aerocivil-cache-sync",
  ],
  [WEATHER_SOURCE_CODES.IDEAM_RADAR]: [],
};

export function getSourceActions(sourceKey) {
  const normalized = String(sourceKey || "").toUpperCase();
  return SOURCE_ACTIONS[normalized] ? [...SOURCE_ACTIONS[normalized]] : [];
}

export function getWeatherSourceActionMetadata(action) {
  return WEATHER_SOURCE_ACTION_METADATA[String(action || "").toLowerCase()] || null;
}

export function getWeatherSourceAdminActionsPlaceholder(sourceKey) {
  const normalized = String(sourceKey || "").toUpperCase();
  const metadata = getWeatherSourceMetadata(normalized);
  const actions = getSourceActions(normalized);

  if (actions.length) {
    return null;
  }

  if (normalized === WEATHER_SOURCE_CODES.IDEAM_RADAR) {
    return {
      title: "No Admin actions available yet for this source.",
      message:
        "Local replay actions are not implemented in Admin yet. Use the server npm scripts to stage, process, and generate tiles.",
      scripts: [
        "npm run weather:ingest-ideam-local-radar-files",
        "npm run weather:process-ideam-radar-files",
        "npm run weather:generate-ideam-radar-tiles",
      ],
    };
  }

  if (metadata?.adminDescription) {
    return {
      title: "No Admin actions available yet for this source.",
      message: metadata.adminDescription,
      scripts: [],
    };
  }

  return {
    title: "No Admin actions available yet for this source.",
    message: "This source does not expose runnable Admin actions.",
    scripts: [],
  };
}

export function getWeatherSourceAdminDescription(sourceKey) {
  const metadata = getWeatherSourceMetadata(sourceKey);
  if (metadata?.adminDescription) {
    return metadata.adminDescription;
  }

  switch (String(sourceKey || "").toUpperCase()) {
    case COLOMBIA_IDEAM_LIVE_SOURCE_KEY:
      return "Live Colombia radar source. Uses the official IDEAM ArcGIS MapServer overlay.";
    case WEATHER_SOURCE_CODES.IDEAM_RADAR:
      return "Historical/local radar replay source. Requires staged RAW radar files and generated tiles. This is not the live Colombia radar source.";
    case COLOMBIA_RAINVIEWER_FALLBACK_SOURCE_KEY:
      return "Fallback radar source used when IDEAM live radar is unavailable.";
    case IDEAM_AWS_RADAR_ARCHIVE_SOURCE_KEY:
      return "IDEAM archive/catalog source for historical radar research.";
    case IDEAM_OSPA_VISUALIZER_SOURCE_KEY:
      return "IDEAM OSPA visualizer and discovery reference path.";
    case SATELLITE_PRECIPITATION_FALLBACK_SOURCE_KEY:
      return "Satellite precipitation fallback source.";
    case SIATA_GEOPORTAL_SOURCE_KEY:
      return "SIATA live/local geospatial source.";
    case AEROCIVIL_AVIATION_RADAR_SOURCE_KEY:
      return "Experimental Aerocivil aeronautical radar — independent layer, not Valley Preferred.";
    default:
      return metadata?.description || null;
  }
}

export function getWeatherSourceActionLabel(action) {
  return getWeatherSourceActionMetadata(action)?.label || action;
}

export function getWeatherSourceDisplayLabel(sourceKey) {
  return getWeatherSourceDisplayName(sourceKey, sourceKey);
}
